<template>
  <div>
    <div
      :class="[
        'payment-items-summary d-flex py-5',
        !readonly ? 'justify-space-between' : 'justify-end'
      ]"
    >
      <button
        v-if="!readonly"
        :disabled="isSavePlayerRegistrationPromoCodeAndPaymentPlanPending"
        class="align-self-start primary--text"
        v-show="!promoCodeButtonHidden"
        type="button"
        @click="handleAddOrRemovePromoCode"
      >
        {{ renderPromoCodeButtonTitle }}
      </button>
      <v-spacer v-if="promoCodeButtonHidden" />
      <div>
        <div
          v-if="renderPromoCode.name"
          class="payment-items-summary__price-item"
        >
          <span class="payment-items-summary__price-name"
            >{{ $t("player_registration.promo") }}:
            {{ renderPromoCode.name }}</span
          >
          <span>- {{ renderPrice(renderPromoCode.discount) }}</span>
        </div>
        <div class="payment-items-summary__price-item">
          <span class="payment-items-summary__price-name">{{
            $t("player_registration.sub_total")
          }}</span>
          <span>{{ renderPrice(playerRegistrationSummary.subTotal) }}</span>
        </div>
        <div class="payment-items-summary__price-item">
          <span class="payment-items-summary__price-name">{{
            $t("player_registration.processing_fee")
          }}</span>
          <span>{{
            renderPrice(playerRegistrationSummary.processingFee)
          }}</span>
        </div>
      </div>
      <AddPromoCodeModal
        :approveAction="promoCodeName => handleSetPromoCode(promoCodeName)"
        :cancelAction="handleCloseAddPromoCodeModal"
        :errorMessage="promoCodeErrorMessage"
        :isOpen="isAddPromoCodeModalOpen"
        :loading="isSavePlayerRegistrationPromoCodeAndPaymentPlanPending"
        class="add-promo-code-modal"
      />
    </div>
    <div
      :class="[
        'd-flex  align-center mt-5',
        playerRegistrationSummary.withPaymentPlan
          ? 'justify-space-between'
          : 'justify-end'
      ]"
    >
      <v-checkbox
        v-if="playerRegistrationSummary.withPaymentPlan"
        :input-value="playerRegistrationSummary.userPickedPaymentPlan"
        :label="$t('player_registration.pay_with_payment_plan')"
        :readonly="isSavePlayerRegistrationPromoCodeAndPaymentPlanPending"
        class="payment-items-summary__payment-plan-checkbox mt-0 font-weight-medium"
        color="secondary"
        hide-details
        @change="handleChangePaymentPlan($event)"
      ></v-checkbox>

      <div class="payment-items-summary__price-item">
        <span class="payment-items-summary__price-name">{{
          $t("player_registration.total")
        }}</span>
        <span>{{ renderPrice(playerRegistrationSummary.total) }}</span>
      </div>
    </div>
    <div
      v-if="
        playerRegistrationSummary.withPaymentPlan &&
          playerRegistrationSummary.paymentMethod ==
            PaymentProvidersTypes.FLUIDPAY
      "
    >
      <span class="payment-items-summary__price-name">{{
        $t("player_registration.store_payment")
      }}</span>
    </div>
  </div>
</template>

<script>
import { prepareLocalString } from "../../../helpers/prepareLocalString";
import { i18n } from "../../../plugins/i18n";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "../../../store/ModuleTypes";
import AddPromoCodeModal from "./AddPromoCodeModal";
import { PaymentProvidersTypes } from "../enums/PaymentProvidersTypes";

export default {
  name: "PaymentConfirmationSummary",
  components: {
    AddPromoCodeModal
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    authorizePayPalPayment: {
      type: Function,
      default: null
    }
  },
  data: () => ({
    isAddPromoCodeModalOpen: false,
    promoCodeErrorMessage: "",
    PaymentProvidersTypes,
    isSavePlayerRegistrationPromoCodeAndPaymentPlanPending: false
  }),
  computed: {
    ...mapGetters(ModuleTypes.PLAYER_REGISTRATION, [
      "playerRegistrationSummary",
      "registrationPlayerId"
    ]),
    renderPromoCodeButtonTitle() {
      if (this.renderPromoCode?.name) {
        return `${i18n.tc("player_registration.remove_promo_code")}`;
      }
      return `${i18n.tc("player_registration.add_promo_code")}`;
    },

    renderPromoCode() {
      return {
        name: this.playerRegistrationSummary?.promoCode,
        discount: this.playerRegistrationSummary?.promoCodeDiscount
      };
    },

    promoCodeButtonHidden() {
      return (
        this.playerRegistrationSummary.subTotal === 0 &&
        this.renderPromoCodeButtonTitle ===
          `${i18n.tc("player_registration.add_promo_code")}`
      );
    }
  },

  methods: {
    ...mapMutations(ModuleTypes.PLAYER_REGISTRATION, [
      "setPlayerRegistrationSummary",
      "setStripePaymentIntentDetails"
    ]),
    ...mapActions(ModuleTypes.PLAYER_REGISTRATION, [
      "savePlayerRegistrationPromoCodeAndPaymentPlan",
      "createStripePaymentIntent"
    ]),

    renderPrice(priceInCents = 0) {
      const price = priceInCents / 100;
      return `${prepareLocalString(price)}`;
    },

    async handleSetPromoCode(
      promoCode = null,
      e = this.playerRegistrationSummary.userPickedPaymentPlan
    ) {
      this.promoCodeErrorMessage = "";
      this.isSavePlayerRegistrationPromoCodeAndPaymentPlanPending = true;
      await this.savePlayerRegistrationPromoCodeAndPaymentPlan({
        registrationPlayerId: this.registrationPlayerId,
        ...(promoCode ? { promoCode } : {}),
        userPickedPaymentPlan: e,
        successCallback: () => {
          this.isAddPromoCodeModalOpen = false;
          this.setPlayerRegistrationSummary({
            playerRegistrationSummary: {
              ...this.playerRegistrationSummary,
              userPickedPaymentPlan: e
            }
          });
          if (
            this.playerRegistrationSummary.paymentMethod ===
            PaymentProvidersTypes.STRIPE
          ) {
            this.createStripePaymentIntent({
              successCallback: ({ data }) => {
                this.setStripePaymentIntentDetails({
                  stripePaymentIntentDetails: data
                });
              }
            });
          }
        },
        failureCallback: ({ data }) => {
          this.promoCodeErrorMessage = data.message;
        }
      });
      this.isSavePlayerRegistrationPromoCodeAndPaymentPlanPending = false;
    },

    handleAddOrRemovePromoCode() {
      if (!this.renderPromoCode?.name) {
        return (this.isAddPromoCodeModalOpen = true);
      }
      this.handleSetPromoCode();
    },

    handleCloseAddPromoCodeModal() {
      this.isAddPromoCodeModalOpen = false;
      this.promoCodeErrorMessage = "";
    },

    handleChangePaymentPlan(e) {
      this.handleSetPromoCode(this.playerRegistrationSummary?.promoCode, e);
      // this.authorizePayPalPayment && this.authorizePayPalPayment();
    }
  }
};
</script>

<style scoped>
.payment-items-summary {
  position: relative;
}

.payment-items-summary:before,
.payment-items-summary:after {
  content: "";
  position: absolute;
  left: -20px;
  right: -20px;
  height: 1px;
  background-color: var(--divider-color);
}

.payment-items-summary:before {
  top: 0;
}

.payment-items-summary:after {
  bottom: 0;
}

.payment-items-summary__payment-plan-checkbox >>> .v-label {
  font-size: 15px;
}

.payment-items-summary__price-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: var(--font-weight-medium);
}
.payment-items-summary__price-name {
  text-align: right;
  margin-right: 32px;
}

.add-promo-code-modal.common-dialog {
  display: none;
}
</style>
